import { useForm } from '@abyss/web/hooks/useForm';
import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { Alert } from '@abyss/web/ui/Alert';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { Layout } from '@abyss/web/ui/Layout';
import find from 'lodash/find';
import React, { Suspense, lazy, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useGetRecommendationPreferencesStatus } from '../../hooks/useGetRecommendationPreferencesStatus';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useAnalyticsStore } from '../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../store/useAnalyticsStore/analyticsStore';
import { useCareTeamStore } from '../../store/useCareTeamStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../store/useDetailsStore';
import { DetailsStore } from '../../store/useDetailsStore/detailsStore';
import { useGuestStore } from '../../store/useGuestStore';
import { GuestState } from '../../store/useGuestStore/guestStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { useVirtualCareStore } from '../../store/useVirtualCareStore';
import { getMemberHealthCoverageType } from '../../utils/providerSearch.utils';
import { getMemberLocation } from '../../utils/psxHeader.utils';
import { loginNotRequired } from '../../utils/session.utils';
import {
  getCurrentMember,
  useIsEligibleForStandaloneDental,
} from '../../utils/user.utils';
import { MaximumProviderLimitBanner } from '../BookmarkButton/MaximumProviderLimitBanner';
import { PSXHeaderCardWrapper } from '../CardWrapper';
import { ChipsCategory } from '../ChipsCategory';
import {
  CareCategories,
  Constants,
  CoverageTypesCodes,
  LOB,
  RESULTS_PAGE_BREADCRUMB_TITLES,
  SEARCH_RESULTS_PAGE,
} from '../Constants';
import { FeatureFlags, SECTION_TERM } from '../ConstantsFeatureFlags';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { mobileOnly, phoneOnly } from '../ConstantsStyles';
import { TabsCategory } from '../TabsCategory';
import { getFormattedDate } from '../Utils/datesUtils/formatDate';
import { isValidCareCategoryWithSectionType } from '../Utils/getProviderType';
import { findLoggedInMember } from '../Utils/memberUtils/memberUtils';
import { SmartChoiceWidget } from '../Widget/SmartChoiceWidget/SmartChoiceWidget';
import { Breadcrumb } from './Breadcrumb';
import { DemoHeader } from './DemoHeader/index';
import {
  handleCloseProviderNotFoundAlert,
  onSubmitForm,
  shouldShowCoachMark,
  shouldShowDemoHeader,
  shouldShowSmartChoiceIntro,
} from './helper';
import { MemberSelectionHeader } from './MemberSelectionHeader';
import { MemberInfo } from './ObapiDemo/memberProfile';
import { PreEffectiveAlertV2, smartChoiceFlex } from './PSXHeader.styled';
import { PSXHeaderProps } from './PSXHeader.types';
import { SearchBar } from './SearchBar';
import {
  getFilteredChips,
  isVirtualVisitChipEnabled,
  validateChipsToTabEnabled,
} from './SearchBar/utils';

const ChoosePCPHeader = lazy(async () => ({
  default: (await import('./ChoosePCPHeader/ChoosePCPHeader')).ChoosePCPHeader,
}));

export const PSXHeaderWithSmartChoiceWidget = ({
  breadcrumbs = [],
  customMessage,
  dataTestId = '',
  isMixedResultsPage = false,
  categoryCount = [],
  onMemberIdChange = () => {},
  onMemberDataChange = () => {},
  showChips = true,
  showMemberSelection = true,
  showChoosePCPHeader = false,
  showSearchInputBackButton = false,
  isFullScreenCompareSmartChoice = false,
}: PSXHeaderProps) => {
  const { t } = useTranslation();
  const url = window.location.href;
  const resultsPage = url?.includes('results/mapview');
  const { portalData } = useContext(PortalContext);

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('mapPinRedesign') === 'true') {
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN,
      true
    );
  }

  const [
    enableMapViewEnhancements,
    chipsToTabsEnabled,
    visionChipEnabled,
    dentalChipEnabled,
    disableMemberSelector,
    virtualVisitChipEnabled,
    suppressPsxLinks,
    showVirtualCare,
    enableMemberDefaultLocation,
    suppressVirtualCareQuickCare,
    isVirtualCareSuppressedForEmpire,
    enableSmartChoiceIntroPopup,
    enableVirtualCareEpic,
    isDemoFlagEnabled,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.CARE_CATEGORY_CHIPS_TO_TABS_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.VISION_CHIP_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.DENTAL_CHIP_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_MEMBER_SELECTOR,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_VISIT,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PSX_LINKS_ON_MEMBER_INFO_FAILURE,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[Constants.CHIPS_CATEGORIES.ALL][
      SECTION_TERM.VIRTUAL_CARE
    ],
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MEMBER_DEFAULT_LOCATION,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_EMPIRE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SMART_CHOICE_INTRO_POPUP,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.DEMO_FLAG,
  ]);

  const mobileScreen = useMediaQuery(
    enableMapViewEnhancements && resultsPage ? mobileOnly : phoneOnly
  );
  const isMobileScreen = useMediaQuery(mobileOnly);

  const { MEMBER_LOCATION, ACTIVE_MEMBER_INDEX, LOGGED_IN_MEMBER_INDEX } =
    Constants.STORAGE_KEYS.SESSION;
  const { ZIPCODE } = Constants.STORAGE_KEYS.LOCAL;

  const { navigate } = useRouter();

  const [searchType, setSearchType] = useState('');
  const [providerType, setProviderType] = useState('');
  const obapimembers = useStore(StoreKeys.OBAPI_MEMBERS);
  const big5 = useStore(StoreKeys.PLAN_SELECTOR);
  const [memberProfiles, setMemberProfiles] =
    useState<MemberInfo[]>(obapimembers);
  const [memberLocation, setMemberLocation] = useState<string>(
    storage.session.get(MEMBER_LOCATION) ?? ''
  );
  const [activeMemberIndex, setActiveMemberIndex] = useState(
    Number(storage.session.get(ACTIVE_MEMBER_INDEX)) ?? 0
  );
  const [activeUser, setActiveUser] = useState<MemberInfo>(
    memberProfiles[activeMemberIndex]
  );
  const enableNonSmartChoiceDisclaimer = useFeatureFlag([
    ConstantsLagoon?.FEATURE_FLAGS.ENABLE_NON_SMART_CHOICE_DISCLAIMER,
  ])[0];
  const { setTypeaheadSearchStore, sectionType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
      sectionType: state.typeaheadSearchStore.sectionType, // Extract sectionType
      search: state.typeaheadSearchStore.search, // Extract search
    }))
  );

  const { guestData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestData: state.guestData,
    }))
  );

  const lagoonData = useLagoon('ui-messaging')();

  const memberNotification = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.PRE_EFFECTIVE_NOTIFICATION,
  });

  const specialityPreferenceTable = useLagoon(
    Constants.LAGOON_TABLE.SPECIALITY_PREFERENCE
  )();

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue, setCoverageType, setChipValue } = chipStore;

  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );
  const { setPCPSearchState } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
    }))
  );
  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );
  const { showMaximumProviderLimitBanner } = useCareTeamStore((state) => ({
    showMaximumProviderLimitBanner: state.showMaximumProviderLimitBanner,
  }));
  const [coachmarkWeb, setCoachmarkWebOpen] = useState<boolean>(false);
  const [showProviderNotFoundAlert, setShowProviderNotFoundAlert] =
    useState<boolean>(false);

  const recommendationPreferencesStatusFromStore = useStore(
    StoreKeys.RECOMMENDATION_PREFERENCES
  );

  const setRecommendationPreferences = useStore(
    StoreKeys.SET_RECOMMENDATION_PREFERENCES
  );

  const [preferencesData] = useGetRecommendationPreferencesStatus({});

  const recommendationPreferencesStatus =
    preferencesData?.data?.recommendationPreferencesStatus ||
    recommendationPreferencesStatusFromStore;
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoiceWithChipValue = smartChoiceEligibility.isEnabled;
  const isSmartChoiceFFEnabled = smartChoiceEligibility.isAvailable;
  const isSmartChoice =
    chipValue === CareCategories.ALL
      ? isSmartChoiceFFEnabled
      : isSmartChoiceWithChipValue;

  const currentZipcode = storage.local.get(ZIPCODE);

  const hasSingleBreadcrumb = breadcrumbs.length === 1;
  const isSearchResultsBreadcrumb =
    breadcrumbs[0]?.title === t(SEARCH_RESULTS_PAGE);

  const resultsPageBreadcrumbTitles = [
    t(RESULTS_PAGE_BREADCRUMB_TITLES[0]),
    t(RESULTS_PAGE_BREADCRUMB_TITLES[1]),
    t(RESULTS_PAGE_BREADCRUMB_TITLES[2]),
    t(RESULTS_PAGE_BREADCRUMB_TITLES[3]),
  ];
  const isResultsPageBreadcrumb =
    hasSingleBreadcrumb &&
    !isSearchResultsBreadcrumb &&
    resultsPageBreadcrumbTitles.includes(breadcrumbs[0]?.title);

  const validBreadcrumbTitlesForBoth = [
    t(RESULTS_PAGE_BREADCRUMB_TITLES[0]),
    t(RESULTS_PAGE_BREADCRUMB_TITLES[3]),
  ];
  const hasValidBreadcrumbsForBoth =
    breadcrumbs.length > 1 &&
    validBreadcrumbTitlesForBoth.includes(breadcrumbs[0]?.title);

  const currentMember = getCurrentMember();

  const { eligibilities } = useVirtualCareStore((state) => state);
  const { planToken } = getPlanSelectorVariables();

  const planEligibilities = eligibilities[planToken] ?? [];

  const { eligibility, isIFPEarlyPay, lineOfBusiness } = currentMember || {};
  const medicalCoverage = getMemberHealthCoverageType(
    eligibility,
    CoverageTypesCodes.MEDICAL
  );

  const [, setShowSmartchoiceIntro] = useState(shouldShowSmartChoiceIntro());

  useEffect(() => {
    const shouldShowPopup = shouldShowSmartChoiceIntro();
    setShowSmartchoiceIntro(shouldShowPopup);
  }, [sessionStorage.getItem('hasDismissedPopover')]);

  const showCoachMarkCondition = shouldShowCoachMark(
    enableSmartChoiceIntroPopup,
    recommendationPreferencesStatus
  );

  const displayMemberSelectorOptions: boolean = !(
    disableMemberSelector &&
    isIFPEarlyPay &&
    (isResultsPageBreadcrumb || hasValidBreadcrumbsForBoth)
  );

  const isPreEffective = useCurrentMemberIsPreEffective();
  const enableVirtualVisitChip = isVirtualVisitChipEnabled(
    virtualVisitChipEnabled,
    showVirtualCare,
    isPreEffective,
    suppressVirtualCareQuickCare
  );

  const filteredChips = getFilteredChips(
    currentMember,
    isMixedResultsPage,
    categoryCount,
    visionChipEnabled,
    dentalChipEnabled,
    isVirtualCareSuppressedForEmpire,
    enableVirtualVisitChip,
    planEligibilities,
    enableVirtualCareEpic
  );

  const chipsEnabled = validateChipsToTabEnabled(
    chipsToTabsEnabled,
    isMixedResultsPage
  );

  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();

  const shouldShowChipsCategory =
    showChips && chipsEnabled && !isEligibleForStandaloneDental;

  const shouldShowTabsCategory =
    showChips &&
    chipsToTabsEnabled &&
    !isMixedResultsPage &&
    !isEligibleForStandaloneDental;

  const defaultValues = {
    'care-category': 'all',
    'member-select': activeMemberIndex.toString(),
    search: '',
    includeSpecialityRollupCodes: '',
    searchType: '',
    firstName: 'FNJNMGFGXBTT',
    lastName: 'LNMGJUNOFE',
    groupNumber: '0AZONEX',
    memberId: '162796738',
    dob: '11-29-1999',
    delsysCode: '52',
    portalSource: '',
    userName: '',
    id: '',
    lob: LOB.ENI,
  };

  const isGuest = url.includes('/guest');
  const isGuestWithPlan = url.includes('/guest-with-plan');
  const isGuestPlanSelection = url.includes('/guest-plan-selection');
  const isStandalone = loginNotRequired();

  const defaultValuesForIfpEarlyPay = {
    exchangeId: guestData?.exchangeId || '1234567898',
    ndbLob: guestData?.ndbLob || '907',
    qhpId: guestData?.qhpId || '40702AZ006001901',
    coverageEffectiveDate: guestData?.coverageEffectiveDate || '12-31-2025',
    zipCode: guestData?.zipCode || '85004',
    stateCode: guestData?.stateCode || 'WA',
    language: guestData?.language || 'English',
    coverageType: guestData?.coverageType || 'M',
    portalSource: guestData?.portalSource || 'Ifpearlypay',
    lob: LOB.IFPEarlyPay,
  };

  const [lobFromStorage] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.LOB,
    ''
  );

  const getDefaultValuesForGuest = () => ({
    lob: lobFromStorage?.includes('-guest')
      ? lobFromStorage
      : `${LOB.ENI}-guest`,
  });

  const defaultValuesForGuest = getDefaultValuesForGuest();

  const getDefaultValues = () => {
    if (isGuestPlanSelection) {
      const portalLob =
        portalData?.portalMemberInfo?.memberInfo?.[0]?.lineOfBusiness +
        '-guest';
      return { lob: portalLob };
    }
    if (isGuestWithPlan) {
      return defaultValuesForGuest;
    }
    if (isGuest) {
      return defaultValuesForIfpEarlyPay;
    }
    return defaultValues;
  };

  const form = useForm({
    defaultValues: getDefaultValues(),
  });

  const isSuperUser = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );

  useEffect(() => {
    if (!currentZipcode) {
      form?.setValue('userZip', '07104');
      storage.local.set(ZIPCODE, '07104');
    }
  }, []);

  useEffect(() => {
    const loggedInMemberIndex = findLoggedInMember(memberProfiles, big5.bigN);
    if (!storage.session.get(ACTIVE_MEMBER_INDEX)) {
      storage.session.set(ACTIVE_MEMBER_INDEX, loggedInMemberIndex?.toString());
    }

    if (!storage.session.get(LOGGED_IN_MEMBER_INDEX)) {
      storage.session.set(LOGGED_IN_MEMBER_INDEX, loggedInMemberIndex);
    }
    if (memberProfiles) {
      const memberIndex = Number(storage.session.get(ACTIVE_MEMBER_INDEX));
      form.setValue('member-select', memberIndex.toString());
      const location = getMemberLocation(
        memberProfiles[activeMemberIndex],
        enableMemberDefaultLocation
      );
      storage.session.set(MEMBER_LOCATION, location);
      setMemberLocation(location);
      setActiveMemberIndex(memberIndex);
      setActiveUser(memberProfiles[memberIndex]);
    }
  }, [memberProfiles, activeMemberIndex, lineOfBusiness]);

  const handleSubmit = (data) => {
    onSubmitForm(data, form, navigate, specialityPreferenceTable, {
      chipStore,
      setTypeaheadSearchStore,
      setAnalyticsState,
      setPCPSearchState,
      setChipValue,
      setCoverageType,
      setDetailsStore,
    });
  };

  useEffect(() => {
    if (isSmartChoice && recommendationPreferencesStatus) {
      setRecommendationPreferences(recommendationPreferencesStatus);
    }
  }, [recommendationPreferencesStatus, isSmartChoice]);

  useEffect(() => {
    // Check if hash is providerNotFound and show alert
    if (window.location.hash === '#providerNotFound') {
      setShowProviderNotFoundAlert(true);
    } else {
      setShowProviderNotFoundAlert(false);
    }
  }, [chipValue]);

  const onCloseProviderNotFoundAlert = () => {
    handleCloseProviderNotFoundAlert(setShowProviderNotFoundAlert);
  };

  const displayMobileBreadcrumbs = () => {
    if (mobileScreen && !showSearchInputBackButton && !isIFPEarlyPay) {
      return <Breadcrumb breadcrumbs={breadcrumbs} />;
    }
    return null;
  };
  const coverageStartDate = medicalCoverage?.coverageEffDateRange?.startDate;

  const isValidCategoryWithSectionType = isValidCareCategoryWithSectionType(
    chipValue,
    sectionType
  );

  useEffect(() => {
    if (
      recommendationPreferencesStatus?.hasDismissedCoachMarkWeb !== 'y' &&
      showCoachMarkCondition
    ) {
      setCoachmarkWebOpen(true);
    }
  }, [
    recommendationPreferencesStatus?.hasDismissedCoachMarkWeb,
    setCoachmarkWebOpen,
    showCoachMarkCondition,
  ]);

  useEffect(() => {
    if (coachmarkWeb) {
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          activeElement.blur();
        }
      }, 0);
    }
  }, [coachmarkWeb]);

  const isSmartChoiceGearIconVisibleforDisclaimer =
    isValidCategoryWithSectionType &&
    sectionType &&
    enableNonSmartChoiceDisclaimer;

  return (
    <React.Fragment>
      {isPreEffective && !portalData?.portalConfig?.isGuestUser && (
        <PreEffectiveAlertV2
          children={memberNotification?.message}
          data-testid="member-notification-alert"
          dismissible={false}
          showDivider={false}
          status="info"
          title={`${memberNotification?.title} ${getFormattedDate(
            coverageStartDate
          )}`}
        />
      )}
      {showProviderNotFoundAlert && (
        <Alert
          data-testid="provider-not-found-alert"
          onClose={onCloseProviderNotFoundAlert}
          title="Provider Not Found"
          variant="error"
        >
          We're sorry. The provider you're looking for is not available in the
          directory.
        </Alert>
      )}
      <PSXHeaderCardWrapper
        backgroundColor={'$bgHeaderColor'}
        showHeaderStyleforMap={!!(enableMapViewEnhancements && resultsPage)}
        showSearchInputBackButton
      >
        {!mobileScreen && !showChoosePCPHeader && !isIFPEarlyPay ? (
          <Breadcrumb breadcrumbs={breadcrumbs} />
        ) : null}
        <FormProvider
          data-auto-testid={dataTestId}
          data-testid={dataTestId}
          onSubmit={handleSubmit}
          state={form}
        >
          <Layout.Stack grow space={0}>
            <Layout.Group alignItems="top" css={{ position: 'relative' }}>
              {displayMobileBreadcrumbs()}
              {showMemberSelection && displayMemberSelectorOptions ? (
                <MemberSelectionHeader
                  activeUser={activeUser}
                  form={form}
                  memberProfiles={memberProfiles}
                  setActiveMemberIndex={setActiveMemberIndex}
                />
              ) : null}
              {showChoosePCPHeader ? (
                <ErrorBoundary>
                  <Suspense fallback={<div>Loading...</div>}>
                    <Layout.Stack grow space={0}>
                      {!mobileScreen && !isIFPEarlyPay ? (
                        <Breadcrumb breadcrumbs={breadcrumbs} />
                      ) : null}
                      <ChoosePCPHeader />
                    </Layout.Stack>
                  </Suspense>
                </ErrorBoundary>
              ) : null}
              {!isEligibleForStandaloneDental && (
                <SmartChoiceWidget
                  breadcrumbs={breadcrumbs}
                  flexCss={{
                    ...smartChoiceFlex(
                      isMobileScreen,
                      showMemberSelection && displayMemberSelectorOptions,
                      isMixedResultsPage,
                      enableMapViewEnhancements && resultsPage,
                      showChoosePCPHeader
                    ),
                    position: 'absolute',
                  }}
                  isFullScreenCompareSmartChoice={
                    isFullScreenCompareSmartChoice
                  }
                  isMixedResultsPage={isMixedResultsPage}
                  isSuperUser={isSuperUser}
                  showChoosePCPHeader={showChoosePCPHeader}
                  showMemberSelection={showMemberSelection}
                />
              )}
            </Layout.Group>
            {shouldShowDemoHeader(
              isStandalone,
              isDemoFlagEnabled,
              enableMapViewEnhancements,
              resultsPage
            ) ? (
              <DemoHeader
                form={form}
                onMemberDataChange={onMemberDataChange}
                onMemberIdChange={onMemberIdChange}
                providerType={providerType}
                searchType={searchType}
                setMemberProfiles={setMemberProfiles}
                setProviderType={setProviderType}
                setSearchType={setSearchType}
              />
            ) : null}
            {!isFullScreenCompareSmartChoice &&
              !(suppressPsxLinks && !currentMember) && (
                <SearchBar
                  breadcrumbs={breadcrumbs}
                  chipsToTabsEnabled={chipsToTabsEnabled}
                  choosePCPHeader={showChoosePCPHeader}
                  isMixedResultsPage={isMixedResultsPage}
                  isSmartChoiceIconVisible={Boolean(
                    (isSmartChoice &&
                      !(showMemberSelection && displayMemberSelectorOptions)) ||
                      isSmartChoiceGearIconVisibleforDisclaimer
                  )}
                  memberLocation={memberLocation}
                  showResultsPageForMap={
                    enableMapViewEnhancements && resultsPage
                  }
                  showSearchInputBackButton={showSearchInputBackButton}
                />
              )}
            {shouldShowChipsCategory && (
              <Layout.Stack grow space={0}>
                <ChipsCategory
                  chips={filteredChips}
                  customMessage={customMessage}
                  data-auto-testid="search-home-chips-category"
                  data-testid="search-home-chips-category"
                  isMixedResultsPage={isMixedResultsPage}
                />
              </Layout.Stack>
            )}
          </Layout.Stack>
        </FormProvider>
        {showMaximumProviderLimitBanner && (
          <MaximumProviderLimitBanner
            isCareTeamView={false}
            isMixedResultsPage={isMixedResultsPage}
          />
        )}
      </PSXHeaderCardWrapper>
      {shouldShowTabsCategory && (
        <Layout.Stack css={{ paddingBottom: '8px' }} grow space={0}>
          <TabsCategory
            chips={filteredChips}
            customMessage={customMessage}
            data-auto-testid="search-home-tabs-category"
            data-testid="search-home-tabs-category"
            isMixedResultsPage={isMixedResultsPage}
          />
        </Layout.Stack>
      )}
    </React.Fragment>
  );
};
